import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Endpoints } from '@/ts/store/Constants';
import ErrorResponse from '@/ts/ErrorResponse';

@Module({name: 'PropertyNotes', namespaced: true})
export default class PropertyNotes extends VuexModule {
    public propertyNotes: any = [];
    public editNoteOverlayVisibility: boolean = false;
    public previewImageVisibility: boolean = false;
    public activeNote: any = null;
    public notePreview: any = null;

    @Mutation
    public RESET_PROPERTY_NOTES_STATE(): void {
        this.propertyNotes             = [];
        this.editNoteOverlayVisibility = false;
        this.previewImageVisibility    = false;
        this.activeNote                = null;
        this.notePreview               = null;
    }

    get getPropertyNotes() {
        return this.propertyNotes;
    }

    get getEditNoteOverlayVisibility() {
        return this.editNoteOverlayVisibility;
    }

    get getPreviewImageVisibility() {
        return this.previewImageVisibility;
    }

    get getActiveNote() {
        return this.activeNote;
    }

    get getNotePreview() {
        return this.notePreview;
    }

    @Action
    public FETCH_PROPERTY_NOTES(propertyId: number) {
        axios.get(Endpoints.Admin.Properties.PROPERTY_NOTES(propertyId))
            .then((response) => {
                store.commit(Mutations.ADD_PROPERTY_NOTES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_NOTE_DETAILS(data: any) {
        axios.get(Endpoints.Admin.Properties.SINGLE_NOTE(data.propertyId, data.noteId))
            .then((response) => {
                store.commit(Mutations.SET_ACTIVE_NOTE, response.data);
                store.commit(Mutations.SET_EDIT_NOTE_OVERLAY_VISIBILITY, true);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_NOTE(data: any) {
        const headers = {
            headers: {
                'Content-Type': 'multipart-data',
            },
        };

        const requestPayload = new FormData();
        requestPayload.append('type', data.type);
        requestPayload.append('title', data.title);
        requestPayload.append('description', data.description);
        requestPayload.append('date', data.date);
        requestPayload.append('private', data.private);
        requestPayload.append('propertyId', data.propertyId);
        requestPayload.append('attachment', data.attachment);

        axios.post(Endpoints.Admin.Properties.PROPERTY_NOTES(data.propertyId), requestPayload, headers)
            .then((response) => {
                store.commit(Mutations.ADD_PROPERTY_NOTE, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_PROPERTY_NOTE(data: any) {
        const headers = {
            headers: {
                'Content-Type': 'multipart-data',
            },
        };

        const requestPayload = new FormData();
        requestPayload.append('type', data.type);
        requestPayload.append('title', data.title);
        requestPayload.append('description', data.description);
        requestPayload.append('date', data.date);
        requestPayload.append('private', data.private);
        requestPayload.append('propertyId', data.propertyId);
        requestPayload.append('fileDeleted', data.fileDeleted);
        requestPayload.append('attachment', data.attachment);
        requestPayload.append('_method', 'PATCH');

        axios.post(Endpoints.Admin.Properties.SINGLE_NOTE(data.propertyId, data.noteId), requestPayload, headers)
            .then((response) => {
                store.commit(Mutations.UPDATE_PROPERTY_NOTE, response.data);

                store.commit(Mutations.SET_EDIT_NOTE_OVERLAY_VISIBILITY, false);
                store.commit(Mutations.SET_ACTIVE_NOTE, null);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_UNIT_NOTE(data) {
        axios.delete(Endpoints.Admin.Properties.SINGLE_NOTE(data.propertyId, data.noteId))
            .then(() => {
                store.commit(Mutations.REMOVE_UNIT_NOTE, data.noteId);
                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_NOTE_PREVIEW_MEDIA(data: any) {
        axios.get(Endpoints.Admin.Properties.PROPERTY_NOTE_MEDIA(data.propertyId, data.noteId))
            .then((response) => {
                if (data.extension === 'application/pdf') {
                    store.commit(Mutations.SET_PREVIEW_MEDIA, response.data);
                    store.commit(Mutations.SET_PREVIEW_MEDIA_VISIBILITY, true);
                } else {
                    store.commit(Mutations.SET_NOTE_PREVIEW, response.data);
                    store.commit(Mutations.SET_PREVIEW_IMAGE_VISIBILITY, true);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public ADD_PROPERTY_NOTES(notes: any) {
        this.propertyNotes = notes;
    }

    @Mutation
    public ADD_PROPERTY_NOTE(notes: any) {
        this.propertyNotes.unshift(notes);
    }

    @Mutation
    public REMOVE_UNIT_NOTE(noteId: number) {
        this.propertyNotes = this.propertyNotes.filter((note: any) => {
            return note.id !== noteId;
        });
    }

    @Mutation
    public SET_ACTIVE_NOTE(note: any) {
        this.activeNote = note;
    }

    @Mutation
    public SET_NOTE_PREVIEW(noteMedia: any) {
        this.notePreview = noteMedia;
    }

    @Mutation
    public SET_PREVIEW_IMAGE_VISIBILITY(visibility: any) {
        this.previewImageVisibility = visibility;
    }

    @Mutation
    public UPDATE_PROPERTY_NOTE(note: any) {
        const updatableNote = this.propertyNotes.find((propertyNote: any) => propertyNote.id === note.id);

        updatableNote.title     = note.title;
        updatableNote.note      = note.note;
        updatableNote.private   = note.private;
        updatableNote.type      = note.type;
        updatableNote.note_date = note.note_date;
        updatableNote.media     = note.media;
        updatableNote.media_extension = note.media_extension;
    }

    @Mutation
    public SET_EDIT_NOTE_OVERLAY_VISIBILITY(visibility: boolean) {
        this.editNoteOverlayVisibility = visibility;
    }
}
